import type { ReactNode } from 'react';
import { useFormat, type Formatter } from '@paige/format';
import type { CostByMeter, Revenue } from './revenue-types';

/**
 * What the admin area means by a cost (docs/pricing.md, "What the admin's cost numbers mean"; Dustin, 2026-10-07):
 * processing cost is the measured card and processor time the work took while in flight, over the pages read;
 * nothing else is divided into a page's cost, and no other cost figure is shown beside it. One label for Paige's own cost, in the
 * words the customer page's money row uses too (money in is "paid a page", never "cost a page").
 */
export const COST_ESTIMATE_LABEL = 'measured processing cost';
export const COST_ESTIMATE_EXPLAINED =
  "Measured: the card and processor time each company's pages and documents took while in flight, at what we pay for it, ÷ pages read in the period. Not in it: storage, credit corrections, and work on Ademero.";
export const ON_ADEMERO_EXPLAINED =
  "Measured in the period and charged to no company: job setup, training sessions, the tour's sample pages, break sheets and attempts that failed. Not in any company's cost a page.";
export const STORAGE_EXPLAINED =
  "Measured: the period's storage debits, in dollars. Kept apart from processing cost and from the cost a page.";
export const MARGIN_EXPLAINED = 'Net collected less processing cost, over net, for the period.';

const METERS: { key: keyof CostByMeter; label: string }[] = [
  { key: 'read', label: 'reading' },
  { key: 'understanding', label: 'understanding' },
  { key: 'review', label: 'AI review' },
  { key: 'other', label: 'other' },
];

/**
 * A page's cost in dollars to five decimals, "$0.00048": the digits the figure of record is quoted in
 * ($0.00034). Four decimals rounded October's $0.00048 to "$0.0005" and its meters to "$0.000".
 */
export function dollarsAPage(_f: Formatter, n: number): string {
  return `$${n.toFixed(5)}`;
}

/** "$0.00048 a page ($0.48 per 1,000)": per page first, per 1,000 second. */
export function costWords(f: Formatter, n: number): string {
  return `${dollarsAPage(f, n)} a page (${f.money(n * 1000)} per 1,000)`;
}

/** The period's processing cost a page by meter, over the pages the ledger counted; empty when none were read. */
function meterWords(f: Formatter, meters: CostByMeter, pages: number): string[] {
  if (pages <= 0) return [];
  return METERS.filter((m) => meters[m.key] > 0).map(
    (m) => `${m.label} ${dollarsAPage(f, meters[m.key] / pages)}`,
  );
}

/** Every period's money: what came in, what went back, what was kept, and when money last came in. */
function allTimeWords(f: Formatter, money: Revenue['allTime']): string {
  const last = money.lastPaidAt
    ? `money last came in ${f.date(money.lastPaidAt)}`
    : 'no card payment yet';
  return `All time: ${f.money(money.collected)} collected, ${f.money(money.refunded)} refunded, ${f.money(money.net)} net; ${last}.`;
}

/**
 * "How this is measured", closed until asked for: what the cost is made of, the split by meter, what is
 * on Ademero and storage (apart from every company's cost), and the money of all time. The numbers come
 * from the ledger's totals, never summed from the rows on the page.
 */
export function HowMeasured({ revenue }: { revenue: Revenue }): ReactNode {
  const f = useFormat();
  const meters = meterWords(f, revenue.costByMeter, revenue.totals.pages);
  return (
    <details className="ad-revenue__how">
      <summary>How this is measured</summary>
      <p>{COST_ESTIMATE_EXPLAINED}</p>
      {meters.length ? <p>By meter, a page: {f.list(meters)}.</p> : null}
      {revenue.onAdemero > 0 ? (
        <p title={ON_ADEMERO_EXPLAINED}>
          On Ademero <strong>{f.money(revenue.onAdemero)}</strong>: {ON_ADEMERO_EXPLAINED}
        </p>
      ) : null}
      {revenue.costByMeter.storage > 0 ? (
        <p title={STORAGE_EXPLAINED}>
          Storage <strong>{f.money(revenue.costByMeter.storage)}</strong>, apart from processing
          cost.
        </p>
      ) : null}
      <p>{MARGIN_EXPLAINED}</p>
      <p className="ad-revenue__all-time">{allTimeWords(f, revenue.allTime)}</p>
    </details>
  );
}
